import { Children, isValidElement, type ReactNode } from 'react'

import {
  DashboardMetricGridProvider,
  type DashboardMetricVariant,
} from '@/components/dashboard/dashboard-metric-grid-context'
import {
  resolveDashboardMetricGridClassName,
  type DashboardMetricColumnLayout,
  type DashboardMetricMaxColumns,
} from '@/components/dashboard/dashboard-metric-grid-classes'
import { CURRENT_CLIENT } from '@/constants'
import { resolveDashboardConfig } from '@/lib/resolve-dashboard-config'
import { cn } from '@/lib/utils'

const defaultDashboardConfig = resolveDashboardConfig(CURRENT_CLIENT as { dashboard?: { showMetricIcons?: boolean } })

export type DashboardMetricGridProps = {
  children: ReactNode
  maxColumns?: DashboardMetricMaxColumns
  /** @default 'responsive' — pane-width breakpoints; `density` fits columns to item count. */
  columnLayout?: DashboardMetricColumnLayout
  /** Override when children are conditional or fragmented. */
  itemCount?: number
  /** Show Lucide icons on child metric cards. Defaults from client `dashboard.showMetricIcons`. */
  showIcons?: boolean
  /** `plain` drops Card chrome — label + value on the page background. */
  variant?: DashboardMetricVariant
  /** Replaces the built-in grid classes (tenant-specific ladders). */
  layoutClassName?: string
  className?: string
}

function countMetricGridItems(children: ReactNode): number {
  return Children.toArray(children).filter(
    (child) => child != null && typeof child !== 'boolean' && (isValidElement(child) || typeof child === 'string'),
  ).length
}

export function DashboardMetricGrid({
  children,
  maxColumns = 5,
  columnLayout = 'responsive',
  itemCount,
  showIcons = defaultDashboardConfig.showMetricIcons,
  variant = 'card',
  layoutClassName,
  className,
}: DashboardMetricGridProps) {
  const resolvedItemCount = itemCount ?? countMetricGridItems(children)

  const gridClassName = resolveDashboardMetricGridClassName({
    layoutClassName,
    columnLayout,
    itemCount: resolvedItemCount,
    maxColumns,
  })

  return (
    <DashboardMetricGridProvider value={{ showIcons, variant }}>
      <div className={cn(gridClassName, 'items-stretch', className)}>{children}</div>
    </DashboardMetricGridProvider>
  )
}
